/**
 * E07-F11-T02 — the Review «Jev triage» controls: the button + confirmation, the
 * «Re-triage selected» button + confirmation, and the progress line of the run.
 *
 * Spec: docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.3.
 *
 * No-automatic-AI (decision-log 2026-07-20): nothing paid starts here except from the
 * confirmation dialog's button. Opening the dialog only READS the preview (rows, rows via
 * search, ambiguous Library pairs, price estimate); the run is started by «Start triage».
 * The progress query polls only while a run is active (a read).
 *
 * All presentation rules live in the DOM-free `./researchTriageModel.ts`.
 */
import { useState, type JSX } from 'react';
import type { TriagePreviewResponse, TriageRetriagePreviewResponse } from '@eer/shared';
import { Dialog, DialogFooter } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import {
  useRetriagePreview,
  useStartRetriage,
  useStartTriage,
  useTriagePreview,
  useTriageProgress,
} from './researchReviewApi.js';
import {
  TRIAGE_BUCKET_LABEL,
  triageConfirmSummary,
  triageProgressView,
} from './researchTriageModel.js';

export interface ReviewTriageBarProps {
  readonly requestId: number;
  /** Rows ticked in the table — the «Re-triage selected» set. */
  readonly selectedIds: readonly number[];
  /** Clear the table selection after a re-triage started. */
  readonly onClearSelection: () => void;
}

type DialogMode = 'none' | 'triage' | 'retriage';

/** The confirmation body (shared by the default run and the re-triage). */
function TriageConfirmBody({
  preview,
  loading,
  error,
}: {
  preview: TriagePreviewResponse | TriageRetriagePreviewResponse | undefined;
  loading: boolean;
  error: string | null;
}): JSX.Element {
  if (loading) {
    return (
      <p className="text-sm text-slate-500" data-testid="triage-confirm-loading">
        Counting the rows and the price…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-600" data-testid="triage-confirm-error">
        {error}
      </p>
    );
  }
  if (!preview) return <></>;
  const s = triageConfirmSummary(preview);
  const replacing = 'replacing' in preview ? preview.replacing : null;
  return (
    <div className="flex flex-col gap-2 text-sm text-slate-700" data-testid="triage-confirm-body">
      <p>
        Jev will triage <b data-testid="triage-confirm-rows">{s.rows}</b>{' '}
        {s.rows === 1 ? 'row' : 'rows'}
        {replacing ? (
          <>
            {' '}
            (<b data-testid="triage-confirm-replacing">{replacing}</b> of them replace an earlier
            result)
          </>
        ) : null}
        .
      </p>
      <p>
        <b data-testid="triage-confirm-via-search">{s.viaSearch}</b> of them have no website and go
        through the free search.
      </p>
      {s.ambiguousPairs > 0 ? (
        <p>
          <b data-testid="triage-confirm-pairs">{s.ambiguousPairs}</b> possible duplicates with the
          Library are checked in the same run (a hint only — nothing is linked without your click).
        </p>
      ) : null}
      <p>
        Price: <b data-testid="triage-confirm-price">{s.price}</b>
        <span className="block text-xs text-slate-500" data-testid="triage-confirm-price-note">
          {s.priceNote}
        </span>
      </p>
      {preview.skippedPrefiltered > 0 || preview.skippedTriaged > 0 ? (
        <p className="text-xs text-slate-500" data-testid="triage-confirm-skipped">
          Left out: {preview.skippedPrefiltered} marked by a prefilter
          {preview.skippedTriaged > 0 ? `, ${preview.skippedTriaged} already triaged` : ''}.
        </p>
      ) : null}
      {!s.canStart ? (
        <p className="text-xs text-amber-700" data-testid="triage-confirm-nothing">
          Nothing to triage — every row is marked by a prefilter or already has a result.
        </p>
      ) : null}
    </div>
  );
}

/** The progress line of the latest run (shown while active, and the last result after). */
function TriageProgressLine({ requestId }: { requestId: number }): JSX.Element | null {
  const { data, isError } = useTriageProgress(requestId);
  if (isError) {
    return (
      <span className="text-xs text-red-600" data-testid="triage-progress-error">
        Could not read the triage progress.
      </span>
    );
  }
  if (!data || data.runId === null) return null;
  const view = triageProgressView(data);
  const buckets = (Object.keys(TRIAGE_BUCKET_LABEL) as Array<keyof typeof TRIAGE_BUCKET_LABEL>)
    .filter((b) => data.buckets[b] > 0)
    .map((b) => `${TRIAGE_BUCKET_LABEL[b]} ${data.buckets[b]}`)
    .join(' · ');
  return (
    <div
      className="flex items-center gap-2 text-xs text-slate-600"
      data-testid="triage-progress"
      data-active={view.active ? 'true' : 'false'}
    >
      {view.active ? (
        <>
          <i className="fa-solid fa-spinner fa-spin text-slate-400" aria-hidden />
          <span className="block w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <span
              className="block h-full rounded-full bg-slate-700"
              style={{ width: `${view.percent}%` }}
            />
          </span>
        </>
      ) : (
        <i className="fa-solid fa-filter-circle-dollar text-slate-400" aria-hidden />
      )}
      <span data-testid="triage-progress-text">
        {view.active ? 'Triage running: ' : 'Last triage: '}
        {view.text}
      </span>
      {view.unavailable > 0 ? (
        <span className="text-red-600" data-testid="triage-progress-unavailable">
          — failed rows show «Jev unavailable»; re-triage them to try again
        </span>
      ) : null}
      {buckets ? (
        <span className="text-slate-400" data-testid="triage-progress-buckets">
          ({buckets})
        </span>
      ) : null}
    </div>
  );
}

export function ReviewTriageBar({
  requestId,
  selectedIds,
  onClearSelection,
}: ReviewTriageBarProps): JSX.Element {
  const toast = useToast();
  const [mode, setMode] = useState<DialogMode>('none');
  const preview = useTriagePreview(requestId);
  const retriagePreview = useRetriagePreview(requestId);
  const start = useStartTriage(requestId);
  const retriage = useStartRetriage(requestId);
  // The rows the re-triage dialog was opened for (the selection may change under it).
  const [retriageIds, setRetriageIds] = useState<readonly number[]>([]);

  function openTriage(): void {
    setMode('triage');
    preview.reset();
    preview.mutate();
  }

  function openRetriage(): void {
    if (selectedIds.length === 0) return;
    const ids = [...selectedIds];
    setRetriageIds(ids);
    setMode('retriage');
    retriagePreview.reset();
    retriagePreview.mutate(ids);
  }

  function close(): void {
    setMode('none');
  }

  /** PAID — fires only from the dialog's confirm button. */
  function confirm(): void {
    if (mode === 'triage') {
      start.mutate(undefined, {
        onSuccess: (res) => {
          toast.success(`Jev triage started for ${res.queued} rows.`);
          close();
        },
        onError: (err) => toast.error(err.message || 'Could not start the triage — try again.'),
      });
      return;
    }
    if (mode === 'retriage' && retriagePreview.data) {
      retriage.mutate(
        { rowIds: retriageIds, confirmedRows: retriagePreview.data.rows },
        {
          onSuccess: (res) => {
            toast.success(`Re-triage started for ${res.queued} rows.`);
            onClearSelection();
            close();
          },
          onError: (err) =>
            toast.error(
              err.message ||
                'Could not start the re-triage — the numbers changed, open the dialog again.',
            ),
        },
      );
    }
  }

  const active = mode === 'triage' ? preview : retriagePreview;
  const data = active.data;
  const canStart = data ? triageConfirmSummary(data).canStart : false;
  const pending = start.isPending || retriage.isPending;

  return (
    <div
      className="shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 mb-4 px-5 py-2.5 flex flex-wrap items-center gap-3"
      data-testid="review-triage-bar"
    >
      <button
        type="button"
        data-testid="triage-start"
        onClick={openTriage}
        title="Jev sorts the unmarked rows into buckets (check first / check / doubtful). Paid — you confirm the count and the price first."
        className="px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700"
      >
        <i className="fa-solid fa-filter mr-1" aria-hidden /> Jev triage
      </button>
      <button
        type="button"
        data-testid="triage-retriage-selected"
        disabled={selectedIds.length === 0}
        onClick={openRetriage}
        title="Triage the ticked rows again (replaces their result). Paid — you confirm first."
        className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded text-xs hover:bg-slate-50 disabled:opacity-40"
      >
        <i className="fa-solid fa-rotate mr-1" aria-hidden /> Re-triage selected (
        {selectedIds.length})
      </button>
      <TriageProgressLine requestId={requestId} />

      <Dialog
        open={mode !== 'none'}
        onClose={close}
        title={mode === 'retriage' ? 'Re-triage the selected rows' : 'Jev triage'}
        testId="triage-confirm-dialog"
        maxWidth="md"
        footer={
          <DialogFooter
            onCancel={close}
            onConfirm={confirm}
            confirmLabel={pending ? 'Starting…' : 'Start triage'}
            confirmDisabled={!canStart || pending || active.isPending}
            variant="accent"
            testId="triage-confirm"
          />
        }
      >
        <TriageConfirmBody
          preview={data}
          loading={active.isPending}
          error={
            active.isError ? active.error?.message || 'Could not count the rows — try again.' : null
          }
        />
      </Dialog>
    </div>
  );
}
